// =============================================================================
// NewInspectionScreen — /inspections/new
//
// The one inspection form for the three kinds of gear that carry a tag or a
// due date: rigging (AS/NZS 4488 / AS 1418 prompts), power tools (AS/NZS 3760
// test & tag) and ladders (AS/NZS 1892 walk-around). Two phases:
//   1. Pick — your own gear first, then search anything by code / S/N / name.
//      Reached with ?item=<id>&kind=<kind> it skips straight to the form.
//   2. Form — date · (type) · result · tag · checks · defects · notes · photo.
//
// 22 Oct 2026 (mig 444) — one form, one vocabulary, reads v_equipment_register.
//   - Inspection date: today by default, back-datable up to
//     app.inspection_backdate_days() (14), never into the future. The tag
//     colour defaults from THAT date, not from today (the 12 Oct trap).
//   - Inspection type on rigging: Detailed / Pre-Use / Quarterly. Tag-out is
//     its own button on the item page.
//   - Result on rigging adds Destroy. Power tools and ladders are pass / fail.
//   - Inspector: you. A manager can log it as someone else (the override is
//     enforced in the RPC too).
//   - Power tools: the tester is picked from app.tnt_testers() — anyone with
//     an active, unexpired TEST-AND-TAG licence on their record. The licence
//     number comes from the record; nobody types it.
//   - Ladders go through app.log_ladder_inspection with the seven checks the
//     ladder tab already used ({stiles: ok|defect, …}).
//   - "Already fixed" on a rigging defect marks it resolved on the way in, so
//     a sling re-stitched on the spot doesn't sit as an open defect. (Tools:
//     a failed test is resolved by the next pass, or "Fixed" on the item page.)
//   - Next due is shown before you save so there are no surprises.
// Photos: optional, single, R2 via api-gateway (rigging + power tools).
// =============================================================================

import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Search, AlertTriangle, Check, HardHat, Wrench, AlignJustify,
  ChevronRight, ImagePlus, Trash2, Loader2, CalendarDays,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Field, Input, Spinner, Pill } from "@/components/ui";
import { uploadInspectionPhoto } from "./inspectionPhoto";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import {
  type RegisterRow, type Kind, KIND_LABEL, URGENCY_LABEL, URGENCY_TONE,
  TagChip, standingLine, homeLine, fmtDay,
  useEquipmentRegister, useTntTesters, useBackdateDays,
} from "@/features/equipment/equipmentShared";

type RigResult   = "Pass" | "Restricted Use" | "Fail" | "Quarantine" | "Destroy";
type RigType     = "Quarterly" | "Detailed" | "Pre-Use";
type CheckResult = "" | "pass" | "fail" | "na";

interface CheckState { result: CheckResult; comment: string }
interface ChecklistDef { id: string; label: string }

const RIGGING_CHECKS: ChecklistDef[] = [
  { id: "webbing",       label: "Webbing intact (no tears, snags, abrasion)" },
  { id: "stitching",     label: "Stitching intact (no broken threads)" },
  { id: "hardware",      label: "Hardware undamaged (no deformation, corrosion)" },
  { id: "load_rating",   label: "Load rating legible" },
  { id: "certification", label: "Certification / nameplate visible" },
];
const TOOL_CHECKS: ChecklistDef[] = [
  { id: "cord_plug",  label: "Cord and plug condition OK" },
  { id: "earth",      label: "Earth continuity test passed" },
  { id: "insulation", label: "Insulation resistance OK" },
  { id: "rcd",        label: "RCD trip test passed (if RCD)" },
];
// Same seven as the ladder tab; saved as {key: ok|defect}.
const LADDER_CHECKS: ChecklistDef[] = [
  { id: "stiles", label: "Stiles straight, no cracks, splits or dents" },
  { id: "rungs",  label: "Rungs / treads tight, none bent, missing or worn" },
  { id: "feet",   label: "Feet fitted, non-slip and not worn through" },
  { id: "locks",  label: "Spreaders, locks and hinges work and hold" },
  { id: "ropes",  label: "Extension rope, pulley and clutches sound (or not fitted)" },
  { id: "label",  label: "Load rating label readable — industrial rated" },
  { id: "clean",  label: "Clean — no paint, oil or mud hiding damage" },
];
const CHECKS_BY_KIND: Record<Kind, ChecklistDef[]> = { rigging: RIGGING_CHECKS, power_tool: TOOL_CHECKS, ladder: LADDER_CHECKS };

interface Draft {
  inspection_date:    string;
  inspection_type:    RigType;
  result:             RigResult;          // rigging vocabulary; tools/ladders use Pass | Fail of it
  tag_colour:         string;
  tag_number:         string;
  checks:             Record<string, CheckState>;
  defects_found:      boolean;
  defect_severity:    "" | "Minor" | "Major" | "Critical";
  defect_summary:     string;
  action_required:    string;
  resolved:           boolean;
  findings:           string;
  inspector_employee_id: string;          // "" = me
  tester_employee_id: string;             // power tools: from tnt_testers
}

function localToday(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function shiftDay(key: string, days: number): string {
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(y, m - 1, d + days);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
}
function addMonths(key: string, months: number): string {
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(y, m - 1 + months, d);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
}
// Mirrors app.tag_colour_for(date): the quarter's colour.
function quarterColour(key: string): string {
  const m = Number(key.slice(5, 7));
  return m <= 3 ? "Red" : m <= 6 ? "Green" : m <= 9 ? "Blue" : "Yellow";
}

const BLANK: Draft = {
  inspection_date: localToday(), inspection_type: "Quarterly", result: "Pass",
  tag_colour: "", tag_number: "", checks: {}, defects_found: false, defect_severity: "",
  defect_summary: "", action_required: "", resolved: false, findings: "",
  inspector_employee_id: "", tester_employee_id: "",
};

const KIND_ICON: Record<Kind, typeof HardHat> = { rigging: HardHat, power_tool: Wrench, ladder: AlignJustify };
const KIND_TINT: Record<Kind, string> = { rigging: "bg-accent/12 text-accent", power_tool: "bg-warn/12 text-warn", ladder: "bg-info/12 text-info" };

// -----------------------------------------------------------------------------

export default function NewInspectionScreen() {
  const user     = useRequireAuth();
  const navigate = useNavigate();
  const qc       = useQueryClient();
  const [params] = useSearchParams();
  const me       = user?.employee_id ?? null;
  const isManager = hasRole(user, "manager");

  const initialId   = params.get("item");
  const initialKind = params.get("kind") as Kind | null;
  const [kindFilter, setKindFilter] = useState<Kind | "all">(initialKind ?? "all");
  const [picked, setPicked] = useState<RegisterRow | null>(null);
  const [draft, setDraft]   = useState<Draft>(BLANK);
  const [search, setSearch] = useState("");
  const [error, setError]   = useState<string | null>(null);

  const [photoFileId, setPhotoFileId]       = useState<string | null>(null);
  const [photoPreview, setPhotoPreview]     = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const { data: rows = [], isLoading } = useEquipmentRegister(!!user);
  const { data: backdateDays = 14 } = useBackdateDays();
  const { data: testers = [] } = useTntTesters(picked?.kind === "power_tool");
  const { data: employees = [] } = useQuery({
    queryKey: ["equipment_employees"],
    enabled: isManager && !!picked && picked.kind !== "power_tool",
    queryFn: async () => {
      const { data, error } = await supabase.from("employees").select("employee_id, first_name, last_name").eq("is_active", true).order("first_name");
      if (error) throw error;
      return (data ?? []) as { employee_id: string; first_name: string | null; last_name: string | null }[];
    },
  });

  // ?item= preload → straight to the form
  useEffect(() => {
    if (picked || !initialId || rows.length === 0) return;
    const hit = rows.find(r => r.item_id === initialId && (!initialKind || r.kind === initialKind));
    if (hit) pick(hit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, initialId, initialKind]);

  function pick(r: RegisterRow) {
    setPicked(r);
    const today = localToday();
    setDraft({ ...BLANK, inspection_date: today, tag_colour: quarterColour(today), tester_employee_id: me && r.kind === "power_tool" ? me : "" });
    setError(null);
  }

  // picker lists
  const mine = useMemo(() => rows.filter(r => (kindFilter === "all" || r.kind === kindFilter) && !!me && (r.holder_employee_id === me)), [rows, kindFilter, me]);
  const hits = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return rows.filter(r => (kindFilter === "all" || r.kind === kindFilter) && [r.code, r.asset_tag, r.serial_number, r.display_name, r.make, r.model, r.kit_name, r.holder_name]
      .some(v => (v ?? "").toLowerCase().includes(q))).slice(0, 30);
  }, [rows, search, kindFilter]);

  const minDate = shiftDay(localToday(), -backdateDays);
  const maxDate = localToday();
  const dateOk  = draft.inspection_date >= minDate && draft.inspection_date <= maxDate;

  // next due preview — mirrors the triggers: rigging interval on the item (3 default), tools 3, ladders 3 (pass only)
  const nextDue = useMemo(() => {
    if (!picked) return null;
    const failing = picked.kind === "rigging" ? draft.result !== "Pass" && draft.result !== "Restricted Use" : draft.result !== "Pass";
    if (failing) return null;
    return addMonths(draft.inspection_date, 3);
  }, [picked, draft.inspection_date, draft.result]);

  const myTesterOk = useMemo(() => testers.some(t => t.employee_id === me), [testers, me]);

  // -------------------------------------------------------------------------
  const save = useMutation({
    mutationFn: async () => {
      if (!picked) throw new Error("Pick an item first.");
      if (!dateOk) throw new Error(`The inspection date has to be within the last ${backdateDays} days and not in the future.`);
      const kind = picked.kind;
      const defs = CHECKS_BY_KIND[kind];

      const labelMap: Record<CheckResult, string> = { "": "", pass: "Pass", fail: "Fail", na: "N/A" };
      const lines: string[] = [];
      for (const d of defs) {
        const c = draft.checks[d.id];
        if (!c || !c.result) continue;
        const head = `${d.label}: ${labelMap[c.result]}`;
        lines.push(c.comment.trim() ? `${head} — ${c.comment.trim()}` : head);
      }
      const conditionNotes = [
        lines.length ? "Visual checks:\n" + lines.map(l => `- ${l}`).join("\n") : "",
        draft.findings.trim() ? `Notes:\n${draft.findings.trim()}` : "",
      ].filter(Boolean).join("\n\n");
      const anyCheckFailed = defs.some(d => draft.checks[d.id]?.result === "fail");
      const inspector = draft.inspector_employee_id || null;

      if (kind === "rigging") {
        if (!draft.tag_colour.trim()) throw new Error("Tag colour is required.");
        const { error } = await supabase.schema("app").rpc("log_rigging_inspection", {
          p_payload: {
            rigging_id:        picked.item_id,
            inspection_date:   draft.inspection_date,
            inspection_type:   draft.inspection_type,
            inspector_employee_id: inspector,
            result:            draft.result,
            tag_colour:        draft.tag_colour.trim(),
            tag_number:        draft.tag_number.trim() || null,
            condition_notes:   conditionNotes || null,
            findings:          draft.findings.trim() || null,
            defects_found:     draft.defects_found || anyCheckFailed,
            defect_severity:   draft.defect_severity || null,
            defect_summary:    draft.defect_summary.trim() || null,
            action_required:   draft.action_required.trim() || null,
            resolved:          draft.defects_found && draft.resolved,
            photo_file_ids:    photoFileId ? [photoFileId] : null,
          },
        });
        if (error) throw error;
      } else if (kind === "power_tool") {
        if (!draft.tester_employee_id) throw new Error("Pick the tester — it has to be someone with a current Test & Tag licence on their record.");
        if (!draft.tag_number.trim()) throw new Error("Test tag number is required.");
        if (!draft.tag_colour.trim()) throw new Error("Tag colour is required.");
        const pass = draft.result === "Pass";
        const { error } = await supabase.schema("app").rpc("log_power_tool_test", {
          p_payload: {
            power_tool_id:            picked.item_id,
            tested_at:                `${draft.inspection_date}T12:00:00+10:00`,
            result:                   pass ? "pass" : "fail",
            tester_employee_id:       draft.tester_employee_id,
            tag_colour:               draft.tag_colour.trim(),
            test_tag_number:          draft.tag_number.trim(),
            asset_tag_number:         picked.asset_tag ?? picked.code,
            visual_inspection_result: pass ? "Pass" : "Fail",
            defects_found:            draft.defects_found || anyCheckFailed || !pass,
            action_required:          draft.action_required.trim() || null,
            failure_reason:           draft.defect_summary.trim() || null,
            notes:                    conditionNotes || null,
          },
        });
        if (error) throw error;
      } else {
        const checks: Record<string, string> = {};
        for (const d of LADDER_CHECKS) checks[d.id] = draft.checks[d.id]?.result === "fail" ? "defect" : "ok";
        const fail = draft.result !== "Pass" || anyCheckFailed;
        const defectText = [draft.defect_summary.trim(), ...LADDER_CHECKS.filter(d => draft.checks[d.id]?.result === "fail").map(d => {
          const c = draft.checks[d.id]; return c?.comment.trim() ? `${d.label} — ${c.comment.trim()}` : d.label;
        })].filter(Boolean).join("; ");
        const { error } = await (supabase as any).schema("app").rpc("log_ladder_inspection", {
          p_payload: {
            asset_id:              picked.item_id,
            inspection_date:       draft.inspection_date,
            inspector_employee_id: inspector,
            result:                fail ? "fail" : "pass",
            checks,
            defects_found:         defectText || null,
            action_taken:          [draft.action_required.trim(), draft.findings.trim()].filter(Boolean).join("\n") || null,
          },
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["equipment_register"] });
      qc.invalidateQueries({ queryKey: ["inspection_register"] });
      qc.invalidateQueries({ queryKey: ["inspection_item"] });
      navigate(`/inspections/equipment/${picked!.kind}/${picked!.item_id}`);
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : (err as { message?: string })?.message ?? "Save failed"),
  });

  async function handlePhotoPick(file: File | null) {
    if (!file || !picked || picked.kind === "ladder") return;
    setError(null); setPhotoUploading(true);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
    try {
      const { file_id } = await uploadInspectionPhoto(picked.kind, picked.item_id, file);
      setPhotoFileId(file_id);
    } catch (err: unknown) {
      const e = err as any;
      setError(`Photo upload: ${e?.message || e?.detail || e?.error || "failed"}`);
      setPhotoPreview(null); setPhotoFileId(null);
    } finally { setPhotoUploading(false); }
  }
  function clearPhoto() { setPhotoFileId(null); setPhotoPreview(null); if (photoInputRef.current) photoInputRef.current.value = ""; }

  // -------------------------------------------------------------------------
  // PICKER
  // -------------------------------------------------------------------------
  if (!picked) {
    return (
      <div className="p-4 md:p-6 max-w-2xl mx-auto pb-24">
        <button type="button" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/equipment"))} className="text-text-muted text-sm flex items-center gap-1 hover:text-text mb-2">
          <ArrowLeft size={14}/> Back
        </button>
        <div className="mb-4">
          <h1 className="text-2xl font-semibold tracking-tight">Log an inspection</h1>
          <div className="text-text-muted text-[13px] mt-1">Your gear first. Search anything by code, serial or name.</div>
        </div>

        <div className="flex gap-1.5 mb-3 flex-wrap">
          {(["all", "rigging", "power_tool", "ladder"] as const).map(k => (
            <button key={k} type="button" onClick={() => setKindFilter(k)}
              className={`px-3 py-1.5 rounded-full border text-[12px] font-medium ${kindFilter === k ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted"}`}>
              {k === "all" ? "All" : KIND_LABEL[k]}
            </button>
          ))}
        </div>

        <Card className="mb-4">
          <div className="p-3 flex items-center gap-2">
            <Search size={14} className="text-text-muted"/>
            <input autoFocus type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Code, serial, name, kit…" className="bg-transparent outline-none text-text text-[14px] flex-1"/>
          </div>
        </Card>

        {isLoading ? <div className="flex justify-center py-8"><Spinner/></div> : (
          <>
            {search.trim().length >= 2 && (
              <div className="mb-5">
                <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-2">{hits.length} match{hits.length === 1 ? "" : "es"}</div>
                <div className="space-y-2">{hits.map(r => <PickRow key={`${r.kind}-${r.item_id}`} r={r} me={me} onPick={() => pick(r)}/>)}</div>
              </div>
            )}
            <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-2">My gear ({mine.length})</div>
            {mine.length === 0 ? (
              <Card><div className="p-4 text-[12px] text-text-muted">Nothing allocated to you yet. Search above to find an item by its code.</div></Card>
            ) : (
              <div className="space-y-2">{mine.map(r => <PickRow key={`${r.kind}-${r.item_id}`} r={r} me={me} onPick={() => pick(r)}/>)}</div>
            )}
          </>
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // FORM
  // -------------------------------------------------------------------------
  const kind  = picked.kind;
  const isRig = kind === "rigging";
  const isTool = kind === "power_tool";
  const Icon = KIND_ICON[kind];
  const results: RigResult[] = isRig ? ["Pass", "Restricted Use", "Fail", "Quarantine", "Destroy"] : ["Pass", "Fail"];
  const out = picked.urgency === "out_of_service";

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto pb-24">
      <button type="button" onClick={() => { setPicked(null); setDraft(BLANK); clearPhoto(); }} className="text-text-muted text-sm flex items-center gap-1 hover:text-text mb-2">
        <ArrowLeft size={14}/> Pick a different item
      </button>

      {/* Item */}
      <Card className="mb-3">
        <div className="p-4">
          <div className="flex items-start gap-3">
            {kind === "ladder"
              ? <div className={`w-10 h-10 rounded-md ${KIND_TINT.ladder} flex items-center justify-center shrink-0`}><Icon size={16}/></div>
              : <TagChip colour={picked.tag_colour} number={picked.tag_number} out={out} size="sm"/>}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[15px] leading-tight">{picked.display_name ?? picked.code}</div>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <span className="font-mono text-[11px] text-text-muted">{picked.code}</span>
                {picked.serial_number && <span className="font-mono text-[11px] text-text-muted">· S/N {picked.serial_number}</span>}
                <Pill label={KIND_LABEL[kind]} small/>
                <Pill label={URGENCY_LABEL[picked.urgency]} small tone={URGENCY_TONE[picked.urgency]}/>
              </div>
              <div className="text-[11px] text-text-muted mt-1">
                {[picked.make, picked.model].filter(Boolean).join(" ")}{picked.swl_kg != null && <> · SWL {picked.swl_kg} kg</>} · {homeLine(picked)}
              </div>
              <div className="text-[11px] text-text-dim mt-0.5">{standingLine(picked)}{picked.last_inspector_name && <> · last by {picked.last_inspector_name}</>}</div>
            </div>
          </div>
        </div>
      </Card>

      {/* When + who */}
      <Card className="mb-3">
        <div className="p-4 grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inspection date" hint={`Today, or up to ${backdateDays} days back`}>
              <div className="relative">
                <CalendarDays size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"/>
                <input type="date" value={draft.inspection_date} min={minDate} max={maxDate}
                  onChange={e => { const v = e.target.value; setDraft(d => ({ ...d, inspection_date: v, tag_colour: isRig || isTool ? quarterColour(v) : d.tag_colour })); }}
                  className={`w-full bg-ink border rounded pl-8 pr-2 py-1.5 text-[13px] text-text ${dateOk ? "border-line" : "border-danger"}`}/>
              </div>
            </Field>
            {isRig && (
              <Field label="Type">
                <select value={draft.inspection_type} onChange={e => setDraft(d => ({ ...d, inspection_type: e.target.value as RigType }))} className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text">
                  <option value="Quarterly">Quarterly</option>
                  <option value="Detailed">Detailed</option>
                  <option value="Pre-Use">Pre-use</option>
                </select>
              </Field>
            )}
            {isTool && (
              <Field label="Tester *" hint="Current Test & Tag licence holders">
                <select value={draft.tester_employee_id} onChange={e => setDraft(d => ({ ...d, tester_employee_id: e.target.value }))} className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text">
                  <option value="">— pick —</option>
                  {testers.map(t => <option key={t.employee_id} value={t.employee_id}>{t.employee_name}{t.licence_number ? ` · ${t.licence_number}` : ""}{t.employee_id === me ? " (you)" : ""}</option>)}
                </select>
              </Field>
            )}
            {!isTool && isManager && (
              <Field label="Inspector" hint="Leave as you unless logging for someone">
                <select value={draft.inspector_employee_id} onChange={e => setDraft(d => ({ ...d, inspector_employee_id: e.target.value }))} className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text">
                  <option value="">You</option>
                  {employees.filter(e => e.employee_id !== me).map(e => <option key={e.employee_id} value={e.employee_id}>{[e.first_name, e.last_name].filter(Boolean).join(" ")}</option>)}
                </select>
              </Field>
            )}
          </div>
          {isTool && testers.length > 0 && !myTesterOk && (
            <div className="text-[11px] text-warn">You're not on the Test & Tag list — add the TEST-AND-TAG licence to your record, or pick the licensed tester who did the test.</div>
          )}
          {isTool && testers.length === 0 && (
            <div className="text-[11px] text-danger">Nobody has a current TEST-AND-TAG licence on record. Add one under Licences before logging a test.</div>
          )}
        </div>
      </Card>

      {/* Result */}
      <Card className="mb-3">
        <div className="p-4">
          <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-2">Result</div>
          <div className={`grid gap-2 ${isRig ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"}`}>
            {results.map(r => {
              const on = draft.result === r;
              const cls = r === "Pass" ? "bg-ok/15 border-ok text-ok" : r === "Restricted Use" ? "bg-warn/15 border-warn text-warn" : "bg-danger/15 border-danger text-danger";
              return (
                <button key={r} type="button" onClick={() => setDraft(d => ({ ...d, result: r, defects_found: (r !== "Pass" && r !== "Restricted Use") || d.defects_found }))}
                  className={`px-3 py-2.5 rounded-md border text-[13px] font-medium transition-colors ${on ? cls : "border-line text-text-muted hover:border-accent"}`}>
                  {on && <Check size={12} className="inline mr-1"/>}{r}
                </button>
              );
            })}
          </div>
          <div className="mt-3 text-[12px] text-text-muted">
            {nextDue
              ? <>Next due <span className="text-text font-medium">{fmtDay(nextDue, true)}</span>{isRig && <> · tag colour for this date: <span className="font-mono text-accent">{quarterColour(draft.inspection_date)}</span></>}</>
              : <span className="text-danger">This takes the item out of service until it passes.</span>}
          </div>
        </div>
      </Card>

      {/* Tag — rigging + tools */}
      {(isRig || isTool) && (
        <Card className="mb-3">
          <div className="p-4 grid grid-cols-2 gap-3">
            <Field label="Tag colour *" hint="Defaults from the inspection date">
              <select value={draft.tag_colour} onChange={e => setDraft(d => ({ ...d, tag_colour: e.target.value }))} className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text">
                {["Red", "Green", "Blue", "Yellow"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={`Tag number${isTool ? " *" : ""}`} hint={isRig ? "Blank if the tag has no number" : undefined}>
              <Input type="text" value={draft.tag_number} onChange={e => setDraft(d => ({ ...d, tag_number: e.target.value }))} placeholder="e.g. 2026-Q4-018" mono/>
            </Field>
          </div>
        </Card>
      )}

      {/* Checks */}
      <Card className="mb-3">
        <div className="p-4">
          <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-2">{isTool ? "Tests" : "Checks"}</div>
          <div className="space-y-3">
            {CHECKS_BY_KIND[kind].map(def => {
              const state = draft.checks[def.id] ?? { result: "" as CheckResult, comment: "" };
              return (
                <CheckTriRow key={def.id} label={def.label} result={state.result} comment={state.comment}
                  onResultChange={r => setDraft(d => ({ ...d, checks: { ...d.checks, [def.id]: { result: r, comment: state.comment } }, defects_found: d.defects_found || r === "fail" }))}
                  onCommentChange={c => setDraft(d => ({ ...d, checks: { ...d.checks, [def.id]: { result: state.result, comment: c } } }))}/>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Defects */}
      <Card className="mb-3">
        <div className="p-4">
          <label className="flex items-start gap-2 cursor-pointer text-[13px]">
            <input type="checkbox" checked={draft.defects_found} onChange={e => setDraft(d => ({ ...d, defects_found: e.target.checked }))} className="mt-1 accent-accent"/>
            <span className="font-medium">Defects found</span>
          </label>
          {draft.defects_found && (
            <div className="grid gap-3 mt-3">
              {isRig && (
                <Field label="Severity">
                  <select value={draft.defect_severity} onChange={e => setDraft(d => ({ ...d, defect_severity: e.target.value as Draft["defect_severity"] }))} className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text">
                    <option value="">— pick —</option><option value="Minor">Minor</option><option value="Major">Major</option><option value="Critical">Critical (tag out)</option>
                  </select>
                </Field>
              )}
              <Field label="What's wrong?">
                <textarea value={draft.defect_summary} onChange={e => setDraft(d => ({ ...d, defect_summary: e.target.value }))} rows={2} placeholder={isTool ? "e.g. Cut in the lead 300 mm from the plug" : "e.g. Frayed webbing near eye, ~10mm of cut threads"} className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"/>
              </Field>
              <Field label={kind === "ladder" ? "Action taken" : "Action required"}>
                <textarea value={draft.action_required} onChange={e => setDraft(d => ({ ...d, action_required: e.target.value }))} rows={2} placeholder="e.g. Tag out, send for assessment" className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"/>
              </Field>
              {isRig && (
                <label className="flex items-start gap-2 cursor-pointer text-[12px] text-text-muted">
                  <input type="checkbox" checked={draft.resolved} onChange={e => setDraft(d => ({ ...d, resolved: e.target.checked }))} className="mt-0.5 accent-accent"/>
                  <span>Already fixed — don't leave this as an open defect</span>
                </label>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Notes */}
      <Card className="mb-3">
        <div className="p-4">
          <Field label="Notes (optional)">
            <textarea value={draft.findings} onChange={e => setDraft(d => ({ ...d, findings: e.target.value }))} rows={3} placeholder="Anything else worth recording" className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"/>
          </Field>
        </div>
      </Card>

      {/* Photo — rigging + tools */}
      {kind !== "ladder" && (
        <Card className="mb-3">
          <div className="p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">Photo (optional)</div>
                <div className="text-[11px] text-text-muted mt-0.5">Goes on the record and into the PDF. Worth it for defects.</div>
              </div>
              {photoUploading && <Loader2 size={14} className="animate-spin text-accent mt-1"/>}
            </div>
            <input ref={photoInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => void handlePhotoPick(e.target.files?.[0] ?? null)}/>
            {photoPreview ? (
              <div className="relative mt-2 border border-line rounded overflow-hidden bg-ink">
                <img src={photoPreview} alt="Inspection photo" className="w-full max-h-72 object-contain"/>
                <button type="button" onClick={clearPhoto} disabled={photoUploading} className="absolute top-2 right-2 p-2 rounded bg-black/60 text-white hover:bg-black/80 disabled:opacity-50" aria-label="Remove photo"><Trash2 size={14}/></button>
              </div>
            ) : (
              <button type="button" onClick={() => photoInputRef.current?.click()} disabled={photoUploading}
                className="w-full mt-2 flex items-center justify-center gap-2 py-6 border border-dashed border-line rounded text-[13px] text-text-muted hover:text-accent hover:border-accent transition-colors disabled:opacity-50">
                <ImagePlus size={16}/><span>Take or upload photo</span>
              </button>
            )}
          </div>
        </Card>
      )}

      {error && (
        <Card className="mb-3 border-danger/30"><div className="p-4 flex items-start gap-3 text-sm text-danger"><AlertTriangle size={16} className="flex-shrink-0 mt-0.5"/><span>{error}</span></div></Card>
      )}

      <StickyBottomBar className="p-3 flex justify-center">
        <div className="flex items-center justify-between gap-2 w-full max-w-2xl">
          <Button variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="primary" icon={Check} onClick={() => { setError(null); save.mutate(); }} disabled={save.isPending || photoUploading || !dateOk}>
            {save.isPending ? "Saving…" : isTool ? "Save test & tag" : "Save inspection"}
          </Button>
        </div>
      </StickyBottomBar>
    </div>
  );
}

// -----------------------------------------------------------------------------

function PickRow({ r, me, onPick }: { r: RegisterRow; me: string | null; onPick: () => void }) {
  const Icon = KIND_ICON[r.kind];
  const out = r.urgency === "out_of_service";
  return (
    <button type="button" onClick={onPick} className="w-full text-left rounded-lg border border-line bg-ink-raised hover:border-accent/50 p-3 transition-colors">
      <div className="flex items-start gap-3">
        {r.kind === "ladder"
          ? <div className={`w-8 h-8 rounded ${KIND_TINT.ladder} flex items-center justify-center shrink-0`}><Icon size={14}/></div>
          : <TagChip colour={r.tag_colour} number={r.tag_number} out={out} size="sm"/>}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[13px] truncate">{r.display_name ?? r.code}</div>
          <div className="text-[11px] text-text-muted mt-0.5 flex items-center gap-1.5 flex-wrap">
            <span className="font-mono">{r.code}</span>
            {r.serial_number && <span className="font-mono">· S/N {r.serial_number}</span>}
            {(r.make || r.model) && <span>· {[r.make, r.model].filter(Boolean).join(" ")}</span>}
          </div>
          <div className="text-[10px] text-text-dim mt-0.5 flex items-center gap-1.5 flex-wrap">
            <span>{homeLine(r)}</span>
            {r.holder_name && <span>· {r.holder_employee_id === me ? "you" : r.holder_name}</span>}
            <Pill label={URGENCY_LABEL[r.urgency]} small tone={URGENCY_TONE[r.urgency]}/>
          </div>
        </div>
        <ChevronRight size={14} className="text-text-dim flex-shrink-0 mt-1"/>
      </div>
    </button>
  );
}

function CheckTriRow({ label, result, comment, onResultChange, onCommentChange }: {
  label: string; result: CheckResult; comment: string; onResultChange: (r: CheckResult) => void; onCommentChange: (c: string) => void;
}) {
  const hint = result === "fail" ? "What's wrong?" : result === "na" ? "Why doesn't it apply?" : "Optional note";
  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <span className="text-[13px] flex-1 leading-tight pt-1">{label}</span>
        <div className="flex items-center gap-1 shrink-0">
          <TriPill label="Pass" active={result === "pass"} tone="ok"     onClick={() => onResultChange(result === "pass" ? "" : "pass")}/>
          <TriPill label="Fail" active={result === "fail"} tone="danger" onClick={() => onResultChange(result === "fail" ? "" : "fail")}/>
          <TriPill label="N/A"  active={result === "na"}   tone="muted"  onClick={() => onResultChange(result === "na" ? "" : "na")}/>
        </div>
      </div>
      {result !== "" && (
        <input type="text" value={comment} onChange={e => onCommentChange(e.target.value)} placeholder={hint} className="w-full mt-1.5 bg-ink border border-line rounded px-2 py-1 text-[12px] text-text"/>
      )}
    </div>
  );
}

function TriPill({ label, active, tone, onClick }: { label: string; active: boolean; tone: "ok" | "danger" | "muted"; onClick: () => void }) {
  const activeClass = tone === "ok" ? "bg-ok/15 border-ok text-ok" : tone === "danger" ? "bg-danger/15 border-danger text-danger" : "bg-ink-raised border-line text-text-muted";
  return (
    <button type="button" onClick={onClick} className={`px-2.5 py-1.5 text-[11px] font-medium rounded border transition-colors min-w-[44px] ${active ? activeClass : "border-line text-text-muted hover:text-text hover:border-accent/40"}`}>
      {label}
    </button>
  );
}
